import React, { FunctionComponent, ReactElement } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { useMedia, createMediaMatcher } from 'react-media-match';
import { Product } from '@/interfaces';
import { Input } from './Input';
import { ModalSelect, ModalSelectLayer } from './ModalSelect';
import { RadioWrapper } from './Radio';
import { Tiles } from './Tiles';
import { productSelectionTheme } from './theme';
import { SetProduct } from './types';
import { useBasket } from '../ProductContext';
import EngravingInput from '../EngravingInput';

interface ProductSelectionProps {
  /** The product to be used for dimension rendering */
  product: Product;
  /** Callback function to set the productId after selection. The dimensions `sku` property is passed as productId */
  setProduct: SetProduct;
  /** If the components renders with this equal to `true` then the contents of the last dimension are opend in `ModalSelectLayer` */
  highlightUnselectedDimension?: boolean;
  /** Callback used to reset `highlightUnselectedDimension` in the outer context. `false` is passed on product selection in the `ModalSelectLayer` */
  setHighlightUnselectedDimension?: (isHightlight: boolean) => void;
}

const ProductSelectionContainer = styled.div<{ mobile: boolean }>`
  padding: 1rem 0;
  ${({ mobile }) => mobile && 'margin: 0 0.75rem;'};
  @media only screen and (max-width: 1024px) {
    margin-right: 1rem;
  }
`;

/**
 * This component renders `Tiles`, `ModalSelect`, `Radio` or `Input` elements to enable selection of a product variation.
 * There is no state handling for the given product. To change the selection provide a new product
 * with updated `isSelected` properties per `dimension`.
 */
const ProductSelection: FunctionComponent<ProductSelectionProps> = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  product: { dimensions, attributes },
  setProduct,
  highlightUnselectedDimension = false,
  setHighlightUnselectedDimension = () => {},
}): ReactElement => {
  const Orientation = createMediaMatcher({
    landscape: '(orientation: landscape)',
    portrait: '(orientation: portrait)',
  });
  const isMobile = useMedia({
    desktop: false,
    mobile: true,
    tablet: Orientation.useMedia({ landscape: false, portrait: true }),
  });
  const { setRequestedSKU, setAddToBasketRequested } = useBasket();

  const element = (
    <ThemeProvider theme={productSelectionTheme}>
      <ProductSelectionContainer data-testid="product-selection" mobile={isMobile}>
        {dimensions.map((dimension) => {
          const { displayName, name, type } = dimension;
          switch (type) {
            case 'TILES':
              return (
                <Tiles
                  dimension={dimension}
                  key={`${name}${displayName}`}
                  setProduct={setProduct}
                />
              );
            case 'INPUT':
              return <Input dimension={dimension} key={`${name}${displayName}`} />;
            case 'RADIO':
              return (
                <RadioWrapper
                  dimension={dimension}
                  key={`${name}${displayName}`}
                  setProduct={setProduct}
                />
              );
            case 'SELECT':
            default:
              return (
                <ModalSelect
                  dimension={dimension}
                  key={`${name}${displayName}`}
                  setProduct={setProduct}
                />
              );
          }
        })}
        {attributes?.engraving && <EngravingInput engravingBlock={attributes?.engraving} />}
        {highlightUnselectedDimension && (
          <ModalSelectLayer
            dimension={[...dimensions.filter((dimension) => dimension.type !== 'INPUT')].pop()} // get last element from dimensions
            setProduct={(prodId: string) => {
              setHighlightUnselectedDimension(false);
              setRequestedSKU(prodId);
              setAddToBasketRequested(true);
            }}
            isModalOpen={highlightUnselectedDimension}
            testOnClose={() => setHighlightUnselectedDimension(false)}
          />
        )}
      </ProductSelectionContainer>
    </ThemeProvider>
  );
  return element;
};

export default ProductSelection;
